import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchCompanies, fetchProjects, resetCache, restoreAdminCache } from 'modules/state-mgmt/cache';
import { getAdminContext, getAdminContextProjectId } from 'utils/adminContext';

/**
 * Hook that verifies and restores the saved admin project context on login.
 *
 * After authentication succeeds, call `verifyAndRestore()` to:
 * 1. Check if a saved admin project context exists in localStorage
 * 2. Fetch the projects list (via Redux thunk) to verify the current user has access
 * 3. If access is confirmed → restore the saved cache snapshot into Redux
 * 4. If no access → clear the wrong cache (but keep the saved snapshot in localStorage for next time)
 *    (IndexedDB workers are preserved; cleanupOldWorkers handles TTL-based deletion)
 * 5. If the API call fails → skip silently (fail-safe)
 */
const useVerifyAdminContext = () => {
  const dispatch = useAppDispatch();
  const currentProjectId = useAppSelector(state => state.cache.project?.id);

  const verifyAndRestore = useCallback(async () => {
    try {
      const savedProjectId = await getAdminContextProjectId();
      if (!savedProjectId) return;

      const result = await dispatch(fetchProjects()).unwrap();
      const projects = result || [];
      const hasAccess = projects.some((p: any) => p.id === savedProjectId);

      // Skip restore if the cache already has the correct project
      if (hasAccess && currentProjectId === savedProjectId) {
        // Fetch companies to ensure that companies are as per the current logged in user's access level
        await dispatch(fetchCompanies({ projectId: savedProjectId, languageCode: 'en' }));
        await dispatch(fetchCompanies({ projectId: savedProjectId, languageCode: 'es' }));
        return;
      }

      if (hasAccess && !currentProjectId) {
        const adminContext = await getAdminContext();
        if (adminContext?.cache) {
          dispatch(restoreAdminCache(adminContext.cache));
          // Fetch companies to ensure that companies are as per the current logged in user's access level
          await dispatch(fetchCompanies({ projectId: savedProjectId, languageCode: 'en' }));
          await dispatch(fetchCompanies({ projectId: savedProjectId, languageCode: 'es' }));
        }
      } else {
        dispatch(resetCache());
      }
    } catch (e) {}
  }, [dispatch, currentProjectId]);

  return { verifyAndRestore };
};

export default useVerifyAdminContext;
